import React, { useEffect, useRef, useCallback, useMemo } from 'react'
import { useSession } from 'next-auth/react'
import { useUser } from '@clerk/nextjs'
import { useFeature } from '@growthbook/growthbook-react'

import { useSelect, useDispatch } from 'store/index'

import { setLoginPromptModalOrigin } from 'reducers/uiState'
import { removeListingData, removeResignedData } from 'reducers/favorites'

import useAugmentedRouter from 'hooks/useAugmentedRouter'

import FavoriteListingCard from 'components/Favorites/FavoritesPage/Cards/FavoriteListingCard'
import Overview from 'components/Pagination/Overview'
import Pagination from 'components/Pagination/Pagination'
import ResignedGrid from 'components/Favorites/FavoritesPage/Grids/ResignedGrid'
import GuestLogin from 'components/Guest/GuestLogin'
import GuestRegisterButton from 'components/Guest/GuestRegisterButton'

import style from './FavoritesGrid.module.scss'

import { pushToDataLayer } from 'utils/Gtm'

import Exclimation from 'assets/icons/icon-exclamationError.svg'

import type { FavoriteListing } from 'types/favorites'

type FavoritesListProps = {
  favorites: FavoriteListing[]
}

const FavoritesGrid: React.FC<FavoritesListProps> = ({ favorites }) => {
  const router = useAugmentedRouter()

  const { data: session } = useSession()

  // Clerk authentication
  const { on: useClerkAuth } = useFeature('clerk-auth')
  const { isSignedIn } = useUser()

  // Determine if user is authenticated (either Clerk or NextAuth)
  const isAuthenticated = useClerkAuth ? isSignedIn : !!session

  // Redux actions
  const appDispatch = useDispatch()

  const gridRef = useRef<HTMLDivElement>(null)

  // Redux selectors
  const resignedListingData = useSelect(
    (state) => state.favorites.resignedListingData,
  )
  const loginPromptModalOrigin = useSelect(
    (state) => state.uiState.loginPromptModalOrigin,
  )

  // Pagination
  const favoritesPerPage = 20
  const totalFavorites = favorites.length ?? 0
  const totalPages = Math.ceil(totalFavorites / favoritesPerPage) ?? 1

  const page = useMemo(
    () => (router.query.page ? parseInt(router.query.page as string) : 1),
    [router.query.page],
  )

  const paginatedFavorites =
    favorites.slice((page - 1) * favoritesPerPage, page * favoritesPerPage) ??
    favorites

  const onPaginate = () => {
    // Remove display data of listings marked for removal on pagination
    if (location.pathname === '/favorites') {
      appDispatch(removeListingData())
      appDispatch(removeResignedData())
    }
    setTimeout(() => {
      gridRef.current &&
        window.scrollTo({
          behavior: 'smooth',
          top: gridRef.current.offsetTop - 70,
        })
    })
  }

  // Clear unselected favorites on navigation/reload
  useEffect(() => {
    if (location.pathname === '/favorites') {
      appDispatch(removeListingData())
      appDispatch(removeResignedData())
    }
  }, [location.pathname, appDispatch])

  // Routing
  const shouldGoBack = useRef(
    !paginatedFavorites.length ||
      (paginatedFavorites &&
        paginatedFavorites.length === 1 &&
        paginatedFavorites[0]?.shouldRemoveData === true),
  )

  // Go to previous page on reload if page contains 1 unselected favorite
  useEffect(() => {
    if (+(router.query.page as string) > 1 && shouldGoBack.current) {
      router.replace(
        `${router.pathname}?page=${+(router.query.page as string) - 1}`,
      )
      shouldGoBack.current = false
    }
  }, [router, router.query.page, router.pathname])

  // Go to previous page if pagination occurs but there aren't enough listings for the next page
  useEffect(() => {
    if (paginatedFavorites && !paginatedFavorites.length && favorites.length) {
      router.replace(
        `${router.pathname}?page=${+(router.query.page as string) - 1}`,
      )
    }
  }, [router, paginatedFavorites, favorites.length])

  // GTM tracking - view create account prompt (favorites page with saveAsGuest)
  useEffect(() => {
    if (!session && favorites.length) {
      appDispatch(setLoginPromptModalOrigin('Favorites Page'))

      if (loginPromptModalOrigin === 'Favorites Page') {
        pushToDataLayer('createAccountPrompt', {
          displayOption: loginPromptModalOrigin,
        })
      }
    }
  }, [appDispatch, session, favorites.length, loginPromptModalOrigin])

  return (
    <>
      <div
        className={`${style.resultsGrid} ${style.favoritesGrid}`}
        id="favoritesGrid"
        ref={gridRef}
      >
        <div className={style.gridHelpers}>
          <Overview
            className="left-aligned"
            itemsPerPage={20}
            page={page}
            totalItems={favorites.length}
          />
        </div>
        {!isAuthenticated && (
          <div className={style.loginWarning}>
            <div className={style.exclimationWarpper}>
              <Exclimation className={style.exclimation} />
            </div>
            <span className={style.warningText}>
              <p>
                Saving without an account means this list may be lost when you
                leave our site.
              </p>
              <GuestRegisterButton
                className={`btn-noStyle ${style.signInBtn}`}
                id="favorites_grid_guest_register"
              />
              {' or '}
              <GuestLogin
                className={`btn-noStyle ${style.signInBtn}`}
                displayText={{
                  isLoggedIn: 'Sign in',
                  isLoggedOut: 'Sign in',
                }}
                id="favorites_grid_guest_login"
              />
            </span>
          </div>
        )}
        {paginatedFavorites &&
          paginatedFavorites.map(
            (listing) =>
              listing && (
                <FavoriteListingCard
                  isFaded={listing.shouldRemoveData ?? false}
                  key={listing.objectID}
                  listing={listing}
                />
              ),
          )}
      </div>
      {+page === +totalPages &&
        resignedListingData &&
        resignedListingData.length > 0 && (
          // Show Resigned Listings on last page
          <ResignedGrid />
        )}
      <div
        className={`${style.paginationWrapper} ${
          totalPages === 1 ? style.topMargin : ''
        }`}
      >
        {paginatedFavorites && paginatedFavorites.length && totalPages > 1 && (
          <Pagination
            itemsPerPage={favoritesPerPage}
            onPaginate={onPaginate}
            page={page}
            totalItems={favorites.length}
            totalPages={totalPages}
          />
        )}
        <Overview
          className="centered"
          itemsPerPage={20}
          page={page}
          totalItems={favorites.length}
        />
      </div>
    </>
  )
}

export default FavoritesGrid
